import { BaseEntityModel } from '/@/modules/core/app/models/abstracts/BaseEntityModel';
import { Estate } from '@/modules/estate/models/Estate';
import { User } from '@/modules/user/models/User';
import { OpportunityItemCollection } from '@/modules/opportunity/models/OpportunityItemCollection';
import { EstateConsent } from '@/modules/estate/models/EstateConsent';
import { EstateAccount } from '@/modules/estateaccount/models/EstateAccount';
import { ModelType } from '@/modules/core/app/constants/ModelType';
import { DocumentExtraction } from '@/modules/ai/models/DocumentExtraction';
import { Feedback } from '@/modules/feedback/models/Feedback';
import Lang from '@/modules/core/app/services/Lang';
import NumeralService from '@/modules/core/app/services/NumeralService';
import { ColumnFormat } from '@/modules/core/app/constants/data.constants';
import dayjs from 'dayjs';
import { DateFormat } from '@/modules/core/app/constants/date.constants';
import { OpportunityScan } from '@/modules/opportunity/models/OpportunityScan';

export class Opportunity extends BaseEntityModel {
  id: number;
  estateId: number;
  opportunityScanId: number;
  type?: string;
  ruleType: string;
  descriptionEn?: string;
  descriptionFr?: string;
  reviewedByUserId?: number;
  reviewedAt?: string;
  titleEn?: string;
  titleFr?: string;
  confidenceTier?: string;
  estimatedValue?: string;
  valueDescriptionEn?: string;
  valueDescriptionFr?: string;

  opportunityScan: Relationship<OpportunityScan>;
  estate?: Relationship<Estate>;
  reviewedByUser?: Relationship<User> | null;
  opportunityItems?: Relationship<OpportunityItemCollection> | null | undefined;
  feedbacks?: Relationship<Feedback[]> | null | undefined;
  documentExtractions?: Relationship<DocumentExtraction[]> | null | undefined;

  constructor(model: Partial<Opportunity> = {}) {
    super(model);

    this.id = model.id!;
    this.estateId = model.estateId!;
    this.opportunityScanId = model.opportunityScanId!;
    this.type = model.type;
    this.ruleType = model.ruleType!;
    this.titleEn = model.titleEn;
    this.titleFr = model.titleFr;
    this.descriptionEn = model.descriptionEn;
    this.descriptionFr = model.descriptionFr;
    this.reviewedByUserId = model.reviewedByUserId;
    this.reviewedAt = model.reviewedAt;
    this.confidenceTier = model.confidenceTier;
    this.estimatedValue = model.estimatedValue;
    this.valueDescriptionEn = model.valueDescriptionEn;
    this.valueDescriptionFr = model.valueDescriptionFr;

    this.opportunityScan = OpportunityScan.mapModel(model.opportunityScan);
    this.estate = Estate.mapModel(model.estate);
    this.reviewedByUser = User.mapModelOrNull(model.reviewedByUser);
    this.opportunityItems = OpportunityItemCollection.mapModel(model.opportunityItems);
    this.feedbacks = Feedback.mapModelsOrNull(model.feedbacks);
    this.documentExtractions = DocumentExtraction.mapModelsOrNull(model.documentExtractions);
  }

  get description(): string | undefined {
    return Lang.isFrench() ? this.descriptionFr : this.descriptionEn;
  }

  getModelName(): ModelType {
    return ModelType.OPPORTUNITY;
  }

  get estateAccounts(): EstateAccount[] {
    return this.estate?.estateAccounts ?? [];
  }

  get estateConsents(): EstateConsent[] {
    return this.estate?.estateConsents ?? [];
  }

  get isReviewed(): boolean {
    return !!this.reviewedByUserId;
  }

  get formattedReviewedAt(): string {
    return this.reviewedAt ? dayjs(this.reviewedAt).format(DateFormat.MONTH_DAY_YEAR) : '';
  }

  get formattedEstimatedValue(): string {
    const estimatedValue: number = this.estimatedValue ? parseFloat(this.estimatedValue) : 0;

    return NumeralService.formatValue(estimatedValue, ColumnFormat.FORMAT_CURRENCY, 2);
  }

  get valueDescription(): string | null {
    return (Lang.isFrench() ? this.valueDescriptionFr : this.valueDescriptionEn) ?? null;
  }

  get title(): string | null {
    return (Lang.isFrench() ? this.titleFr : this.titleEn) ?? null;
  }
}
